/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Icon, Table } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import React, { useState, Fragment } from 'react'
import { UltyMoney } from '@ulty/ulty-ui'
import useUser from '../../../Shared/UserProvider/useUser'
import { useOrderPickingScan } from '../OrderScanPicking'
import { ScanInfo } from '../OrderScanPicking/ScanInfo'
import { FoundItemButton } from './OrderItemFulfillment/FoundItemButton'
import { OrderItemFulfillmentIssueBar } from './OrderItemFulfillment/OrderItemFulfillmentIssueBar/OrderItemFulfillmentIssueBar'
import { FulfillmentState } from './OrderItemFulfillment/FulfillmentState'
import { FulfillmentIssueLabel } from './OrderItemFulfillment/FulfillmentIssueLabel'
import { OrderItemQuantity } from './OrderItemQuantity'
import { useTranslation } from 'react-i18next'


const OrderItemRow = ({
                          displayOrderItemName = true,
                          order,
                          orderItem,
                          onOrderItemDetailsPreview,
                          relatedReplacements,
                          currentQuantity = undefined,
                      }) => {
    const {user} = useUser()
    const [quantity, setQuantity] = useState(currentQuantity ?? orderItem.quantity)
    const {scannedItemsMap} = useOrderPickingScan()
    const [t] = useTranslation();

    if (currentQuantity !== undefined && currentQuantity !== quantity) {
        setQuantity(currentQuantity)
    }

    const buildModifiers = (modifiers, prefix = 0) => {
        return modifiers.map((m, idx) => {
            let Pre = () => null
            if (m.modifiers && m.modifiers.length > 0) {
                Pre = () => buildModifiers(m.modifiers, prefix + 1)
            }

            return (
              <Fragment key={`${m.name}-(${m.quantity}x)-${idx}`}>
                  <div style={{paddingLeft: `${prefix * 10}px`}}>{`- ${m.name} (${m.quantity}x)`}</div>
                  <Pre/>
              </Fragment>
            )
        })
    }

    const OrderItemNameCell = () => {
        if (displayOrderItemName) {
            return <Table.Cell
              css={css`
                display: flex;
                flex-direction: column;
                font-style: italic;

                a {
                    color: rgb(0, 0, 0);
                }

                a:hover {
                    color: #1e70bf;
                }
            `}>
                <a
                  href="#"
                  onClick={onOrderItemDetailsPreview}
                  style={{textDecoration: parseInt(quantity) === 0 ? 'line-through' : 'none'}}
                >{orderItem.name}</a>
                {orderItem.modifiers && buildModifiers(orderItem.modifiers)}
            </Table.Cell>
        } else {
            return <Table.Cell/>
        }
    }

    return (
      <>
          <Table.Row>
              <OrderItemNameCell/>
              <Table.Cell>
                  <div
                    css={css`
                        &:has(*) {
                            margin-bottom: .5rem;
                        }
                    `}
                  >
                      <FulfillmentState
                        orderItem={orderItem}
                        scanValue={scannedItemsMap.get(orderItem?.id)}
                      />

                      {orderItem.fulfillmentIssue &&
                                                  orderItem.fulfillmentIssue !== 'FOUND_ITEM' &&
                                                  orderItem.customerAckType !== 'AWAITING_ACTION' &&
                                                  <FulfillmentIssueLabel orderItem={orderItem}/>
                                                }
                  </div>

                  <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        row-gap: .5rem;
                    `}
                  >
                      {order.isModifiable &&
                        (!orderItem.customerAckType || orderItem.customerAckType === 'AWAITING_ACTION') &&
                        (!orderItem.fulfillmentIssue ||
                          orderItem.fulfillmentIssue === 'PARTIAL_AVAILABILITY' ||
                          orderItem.fulfillmentIssue === 'SUBSTITUTION' ||
                          orderItem.fulfillmentIssue === 'FOUND_ITEM') &&
                        <OrderItemFulfillmentIssueBar
                          order={order}
                          orderItem={orderItem}
                          hasReplacement={relatedReplacements && relatedReplacements.length > 0} 
                        />
                      }

                      {!orderItem.pickingMethod &&
                        (!orderItem.customerAckType || orderItem.customerAckType === 'AWAITING_ACTION') &&
                        (!orderItem.fulfillmentIssue || (['OUT_OF_ITEM', 'SUBSTITUTION'].includes(orderItem.fulfillmentIssue))) &&
                        ['RECEIVED', 'ACCEPTED', 'PREPARING'].includes(order.status) &&
                        <FoundItemButton
                          order={order}
                          orderItem={orderItem}
                        />
                      }
                  </div>
              </Table.Cell>
              <Table.Cell
                textAlign="right"
                className={(orderItem.quantity || 0) > 1 ? 'multi-items' : ''}>
                  <OrderItemQuantity
                    css={orderItem.quantity !== orderItem.initialQuantity ? css`
                            text-decoration: line-through;
                        ` : undefined}
                    orderItem={orderItem}
                    quantity={orderItem.initialQuantity}
                  />

                  {orderItem.quantity !== orderItem.initialQuantity &&
                    <>
                        <Icon
                          style={{margin: '0.3rem', verticalAlign: 'middle'}}
                          size="small"
                          color="black"
                          name="long arrow alternate right"
                        />

                        <OrderItemQuantity
                          orderItem={orderItem}
                          quantity={orderItem.quantity}
                        />
                    </>
                  }
              </Table.Cell>
              <Table.Cell textAlign="right">
                  <UltyMoney
                    readonly
                    amount={orderItem.unitPrice}
                    perUnitOption={orderItem.item && orderItem.item.pricedByUnit?.type !== 'COUNT' ? {
                        value: orderItem.item.pricedByUnit?.unit,
                        text: t(`measurement_unit.symbols.${orderItem.item.pricedByUnit?.unit}`)
                    }: { value: user.getCompanyCurrency(), text: '' }}
                    currencyCode={user.getCompanyCurrency()}
                    locale={user.getCompanyLocale()}
                  />
              </Table.Cell>
              <Table.Cell textAlign="right">
                  {orderItem.totalPrice ?
                    <UltyMoney
                      readonly
                      amount={orderItem.totalPrice}
                      currencyCode={user.getCompanyCurrency()}
                      locale={user.getCompanyLocale()}
                    /> : '-'
                  }
              </Table.Cell>
          </Table.Row>
          {relatedReplacements && relatedReplacements.map(relatedReplacement => (
            <Table.Row>
                <Table.Cell css={css`
                    display: flex;
                    flex-direction: column;
                    font-style: italic;

                    a {
                        color: rgb(0, 0, 0);
                    }

                    a:hover {
                        color: #1e70bf;
                    }
                `}>
                    <div
                      css={css`
                          display: flex;
                      `}
                    >
                        <Icon
                          name="level up alternate"
                          rotated="clockwise"
                          css={css`
                              &.icon {
                                  align-self: center;
                              }
                          `}
                        />
                        <div
                          css={css`
                              display: flex;
                              flex-direction: column;
                          `}
                        >
                            <a
                              href="#"
                              onClick={onOrderItemDetailsPreview}
                              style={{textDecoration: parseInt(relatedReplacement.quantity) === 0 ? 'line-through' : 'none'}}
                            >{relatedReplacement.name}
                            </a>
                        </div>
                    </div>
                </Table.Cell>
                <Table.Cell>
                    <ScanInfo
                      css={css`
                                  &.ui.label {
                                      align-self: flex-start;
                                  }
                              `}
                      orderItem={relatedReplacement}
                      scanValue={scannedItemsMap.get(relatedReplacement?.id)}
                    />
                    <FulfillmentState
                      orderItem={relatedReplacement}
                      scanValue={scannedItemsMap.get(relatedReplacement.id)}
                    />
                   {relatedReplacement.fulfillmentIssue &&
                                               relatedReplacement.fulfillmentIssue !== 'FOUND_ITEM' &&
                                               relatedReplacement.customerAckType !== 'AWAITING_ACTION' &&
                                               <FulfillmentIssueLabel orderItem={relatedReplacement}/>
                                             }
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  className={(relatedReplacement.quantity || 0) > 1 ? 'multi-items' : ''}>
                    <OrderItemQuantity
                      orderItem={relatedReplacement}
                      quantity={relatedReplacement.quantity}
                    />
                </Table.Cell>
                <Table.Cell textAlign="right">
                    <UltyMoney
                      readonly
                      amount={relatedReplacement.unitPrice}
                      perUnitOption={relatedReplacement.item?.pricedByUnit?.type !== 'COUNT' ? {
                          value: relatedReplacement.item.pricedByUnit?.unit,
                          text: t(`measurement_unit.symbols.${relatedReplacement.item.pricedByUnit?.unit}`)
                      }: { value: user.getCompanyCurrency(), text: '' }}
                      currencyCode={user.getCompanyCurrency()}
                      locale={user.getCompanyLocale()}
                    />
                </Table.Cell>
                <Table.Cell textAlign="right">
                    {relatedReplacement.totalPrice ?
                      <UltyMoney
                        readonly
                        amount={relatedReplacement.totalPrice}
                        currencyCode={user.getCompanyCurrency()}
                        locale={user.getCompanyLocale()}
                      /> : '-'
                    }
                </Table.Cell>
            </Table.Row>
          ))}
      </>
    )
}

OrderItemRow.propTypes = {
    order: PropTypes.object.isRequired,
    orderItem: PropTypes.object.isRequired,
    onOrderItemDetailsPreview: PropTypes.func,
    addOrderItemToFulfillmentIssues: PropTypes.func,
    fulfillmentReplacement: PropTypes.object,
}

export default OrderItemRow
